import { useEffect } from "react";
import { useAuth } from "./AuthContext";
import { authFetchWithRefresh } from "./authApi";

const REPORT_INTERVAL_MS = 60_000;
const BROWSER_KEY = "sdb-user-activity-browser";
const UUID_V4 =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
let fallbackBrowserId: string | undefined;

function readBrowserId(): string {
  try {
    const existing = localStorage.getItem(BROWSER_KEY);
    if (existing && UUID_V4.test(existing)) return existing;
    const id = crypto.randomUUID();
    localStorage.setItem(BROWSER_KEY, id);
    return id;
  } catch {
    // Continue tracking within this tab when storage is unavailable.
    return (fallbackBrowserId ??= crypto.randomUUID());
  }
}

async function getBrowserId(): Promise<string> {
  // Coordinate simultaneous first visits in separate tabs where supported.
  if (navigator.locks) {
    return navigator.locks.request(BROWSER_KEY, readBrowserId);
  }
  return readBrowserId();
}

/** Records visits and interaction, without collecting routes or input content. */
export function UserActivityTracker() {
  const { user } = useAuth();
  const userId = user?.id;

  useEffect(() => {
    if (!userId) return;

    let dirty = true;
    let lastInteractionAt = Date.now();
    let inFlight = false;
    let disposed = false;
    let lastAttempt: number | undefined;

    const report = async () => {
      if (
        disposed ||
        inFlight ||
        !dirty ||
        Date.now() - lastInteractionAt >= 2 * REPORT_INTERVAL_MS ||
        document.visibilityState !== "visible" ||
        (lastAttempt !== undefined &&
          Date.now() - lastAttempt < REPORT_INTERVAL_MS)
      ) {
        return;
      }

      dirty = false;
      inFlight = true;
      lastAttempt = Date.now();
      try {
        const browserId = await getBrowserId();
        if (disposed || document.visibilityState !== "visible") {
          dirty = true;
          return;
        }
        await authFetchWithRefresh<void>("/users/activity", {
          method: "POST",
          body: JSON.stringify({ browserId })
        });
      } catch {
        // Retry on the next interval; analytics must not disrupt the site.
        dirty = true;
      } finally {
        inFlight = false;
      }
    };

    const interact = () => {
      if (document.visibilityState !== "visible") return;
      dirty = true;
      lastInteractionAt = Date.now();
      void report();
    };
    const events = ["pointerdown", "pointermove", "keydown", "scroll"] as const;
    for (const event of events) {
      window.addEventListener(event, interact, { passive: true });
    }
    document.addEventListener("visibilitychange", interact);
    const timer = window.setInterval(() => void report(), REPORT_INTERVAL_MS);
    void report();

    return () => {
      disposed = true;
      window.clearInterval(timer);
      for (const event of events) window.removeEventListener(event, interact);
      document.removeEventListener("visibilitychange", interact);
    };
  }, [userId]);

  return null;
}
